import { Download, Heart, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { openDocument } from "@/features/documents/api";
import { useClientForms } from "@/features/forms/hooks/use-client-forms";
import {
  type FormCategory as ApiFormCategory,
  type FormJurisdiction,
  type FormSubmission,
  GENERATABLE_FORMS,
} from "@/features/forms/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

type FormStatus = "Not Started" | "Draft" | "Completed";

interface IrsFormEntry {
  taxFormId: string;
  formNumber: string;
  /** Bare number (e.g. "2848") for building the fill-page route. */
  rawFormNumber: string;
  title: string;
  status: FormStatus;
  generatable: boolean;
  documentId: string | null;
  submissionId: string | null;
  isFavorite: boolean;
}

type LibraryFilter = "all" | "favorites" | "recent";

interface FormCategory {
  id: string;
  label: string;
  count: number;
  forms: IrsFormEntry[];
}

const activeFilterPills: { key: LibraryFilter; label: string }[] = [
  { key: "all", label: "All Forms" },
  { key: "favorites", label: "Favorites" },
  { key: "recent", label: "Recent" },
];
// Jurisdiction (registry flag) and work-status (latest submission) filters; each
// pill toggles — clicking the active one clears it.
const jurisdictionPills: { key: FormJurisdiction; label: string }[] = [
  { key: "FEDERAL", label: "Federal" },
  { key: "STATE", label: "State" },
];
const statusPills: FormStatus[] = ["Draft", "Completed"];

// Registry category → the design's section id + label, in display order.
const CATEGORY_META: { key: ApiFormCategory; id: string; label: string }[] = [
  { key: "POA_AUTHORIZATION", id: "power-of-attorney", label: "Power of Attorney" },
  { key: "PAYMENT_PLAN", id: "installment-agreements", label: "Installment Agreements" },
  { key: "COLLECTION_INFO", id: "collection-info-statements", label: "Collection Info Statements" },
  { key: "OFFER_IN_COMPROMISE", id: "offer-in-compromise", label: "Offer in Compromise" },
  { key: "RELIEF", id: "relief", label: "Relief" },
  { key: "APPEALS", id: "appeals", label: "Appeals" },
  { key: "OTHER", id: "other", label: "Other" },
];

const STATUS_LABEL: Record<FormSubmission["status"], FormStatus> = {
  NOT_STARTED: "Not Started",
  DRAFT: "Draft",
  COMPLETED: "Completed",
};

function statusActionLabel(status: FormStatus) {
  if (status === "Draft") return "Resume";
  return "Open";
}

export default function ClientIrsFormsPage() {
  const { client } = useActiveClient();
  const navigate = useNavigate();
  const { taxForms, submissions, favorites, recentFormNumbers, loading, error, toggleFavorite } =
    useClientForms(client.id);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<LibraryFilter>("all");
  const [jurisdiction, setJurisdiction] = useState<FormJurisdiction | null>(null);
  const [statusFilter, setStatusFilter] = useState<FormStatus | null>(null);

  const categories = useMemo<FormCategory[]>(() => {
    // Latest submission per form number (submissions arrive newest-first).
    const latest = new Map<string, FormSubmission>();
    for (const sub of submissions) {
      if (!latest.has(sub.formNumber)) latest.set(sub.formNumber, sub);
    }
    const term = search.trim().toLowerCase();
    const isBusiness = client.type === "Business";

    return CATEGORY_META.map((meta) => {
      const forms: IrsFormEntry[] = taxForms
        .filter(
          (f) =>
            f.isActive &&
            f.category === meta.key &&
            (isBusiness ? f.appliesToBusinesses : f.appliesToIndividuals) &&
            (!term ||
              f.formNumber.toLowerCase().includes(term) ||
              f.title.toLowerCase().includes(term)) &&
            (activeFilter !== "favorites" || favorites.has(f.id)) &&
            (activeFilter !== "recent" || recentFormNumbers.has(f.formNumber)) &&
            (!jurisdiction || f.jurisdiction === jurisdiction),
        )
        .map((f) => {
          const sub = latest.get(f.formNumber);
          return {
            taxFormId: f.id,
            formNumber: `Form ${f.formNumber}`,
            rawFormNumber: f.formNumber,
            title: f.title,
            status: sub ? STATUS_LABEL[sub.status] : "Not Started",
            generatable: GENERATABLE_FORMS.has(f.formNumber),
            documentId: sub?.generatedDocument?.id ?? null,
            submissionId: sub?.id ?? null,
            isFavorite: favorites.has(f.id),
          };
        })
        .filter((e) => !statusFilter || e.status === statusFilter);
      return { id: meta.id, label: meta.label, count: forms.length, forms };
    });
  }, [
    taxForms,
    submissions,
    search,
    client.type,
    activeFilter,
    jurisdiction,
    statusFilter,
    favorites,
    recentFormNumbers,
  ]);

  const visibleCategories = categories.filter((c) => c.forms.length > 0);

  function openForm(form: IrsFormEntry) {
    // Form numbers carry spaces/parens (e.g. "433-A (OIC)") — encode the segment.
    navigate(`/client/${client.id}/irs-forms/${encodeURIComponent(form.rawFormNumber)}`);
  }

  function viewDocument(documentId: string) {
    openDocument(documentId).catch(() => toast.error("Couldn't open the PDF."));
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Search & filters */}
      <Card className="gap-6 p-6">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-4 size-5 -translate-y-1/2" />
          <Input
            type="text"
            placeholder="Search by Form Number (e.g. 2848), Name, or Keywords..."
            className="h-14 rounded-xl pl-12 text-base"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterPills.map((pill) => (
            <button
              key={pill.key}
              type="button"
              aria-pressed={activeFilter === pill.key}
              onClick={() => setActiveFilter(pill.key)}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-medium transition-colors",
                activeFilter === pill.key
                  ? "bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted border",
              )}
            >
              {pill.label}
            </button>
          ))}
          {jurisdictionPills.map((pill) => (
            <button
              key={pill.key}
              type="button"
              aria-pressed={jurisdiction === pill.key}
              onClick={() =>
                setJurisdiction((cur) => (cur === pill.key ? null : pill.key))
              }
              className={cn(
                "rounded-full px-4 py-2 text-xs font-medium transition-colors",
                jurisdiction === pill.key
                  ? "bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted border",
              )}
            >
              {pill.label}
            </button>
          ))}
          <div className="bg-border mx-2 h-6 w-px" />
          {statusPills.map((pill) => (
            <button
              key={pill}
              type="button"
              aria-pressed={statusFilter === pill}
              onClick={() => setStatusFilter((cur) => (cur === pill ? null : pill))}
              className={cn(
                "rounded-full px-4 py-2 text-xs font-medium transition-colors",
                statusFilter === pill
                  ? "bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted border",
              )}
            >
              {pill}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive py-8 text-sm">{error}</p>
      ) : (
        /* Layout with jump navigation */
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-12">
          {/* Categories jump nav */}
          <div className="hidden xl:col-span-3 xl:block">
            <Card className="sticky top-[144px] gap-1 p-4">
              <h4 className="text-foreground mb-2 px-2 text-xs font-bold tracking-wider uppercase">
                Categories
              </h4>
              <nav className="space-y-1">
                {visibleCategories.map((category, index) => (
                  <a
                    key={category.id}
                    href={`#${category.id}`}
                    className={cn(
                      "flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      index === 0
                        ? "bg-primary/5 text-primary"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    <span>{category.label}</span>
                    <span
                      className={cn(
                        "rounded px-2 py-0.5 text-xs",
                        index === 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {category.count}
                    </span>
                  </a>
                ))}
              </nav>
            </Card>
          </div>

          {/* Category sections */}
          <div className="flex flex-col gap-10 xl:col-span-9">
            {visibleCategories.map((category) => (
              <section key={category.id} id={category.id} className="scroll-mt-[144px]">
                <h3 className="text-foreground mb-4 border-b pb-2 text-xl font-bold">
                  {category.label}
                </h3>
                <Card className="gap-0 overflow-hidden p-0">
                  <Table>
                    <TableBody>
                      {category.forms.map((form) => (
                        <TableRow key={form.taxFormId}>
                          <TableCell className="w-32 align-middle">
                            <Badge
                              variant="outline"
                              className="border-primary/20 bg-primary/10 text-primary font-bold"
                            >
                              {form.formNumber}
                            </Badge>
                          </TableCell>
                          <TableCell className="align-middle">
                            {form.generatable ? (
                              <button
                                type="button"
                                onClick={() => openForm(form)}
                                className="hover:text-primary text-foreground text-left font-semibold hover:underline"
                              >
                                {form.title}
                              </button>
                            ) : (
                              <span className="text-muted-foreground font-medium">{form.title}</span>
                            )}
                          </TableCell>
                          <TableCell className="align-middle">
                            <div className="flex items-center justify-end gap-2">
                              {form.status === "Completed" ? (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!form.documentId}
                                    onClick={() => form.documentId && viewDocument(form.documentId)}
                                  >
                                    View Final
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label="Download"
                                    disabled={!form.documentId}
                                    onClick={() => form.documentId && viewDocument(form.documentId)}
                                  >
                                    <Download />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="sm"
                                  disabled={!form.generatable}
                                  onClick={form.generatable ? () => openForm(form) : undefined}
                                >
                                  {form.generatable ? statusActionLabel(form.status) : "Coming soon"}
                                </Button>
                              )}
                              <button
                                type="button"
                                aria-label={`Favorite ${form.formNumber}`}
                                aria-pressed={form.isFavorite}
                                onClick={() => toggleFavorite(form.taxFormId)}
                                className={cn(
                                  "transition-colors",
                                  form.isFavorite
                                    ? "text-destructive"
                                    : "text-muted-foreground hover:text-destructive",
                                )}
                              >
                                <Heart className={cn("size-5", form.isFavorite && "fill-current")} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </section>
            ))}
            {visibleCategories.length === 0 && (
              <p className="text-muted-foreground py-8 text-sm">
                {activeFilter === "favorites"
                  ? "No favorited forms yet — tap the heart on a form to add it."
                  : activeFilter === "recent"
                    ? "No recently opened forms for this client yet."
                    : "No forms match this client type."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
